import { useQuery } from "@tanstack/react-query";
import { CreditCard } from "lucide-react";
import { useLocale, useTranslations } from "use-intl";

import { Skeleton } from "@lawline/ui/components/skeleton";

import { ExternalLink } from "@platform/components/ui/external-link";
import { Notification } from "@platform/components/ui/notification";
import { docsLink } from "@platform/core/docs";
import { billingQueryOptions } from "@platform/features/billing/billing.queries";
import { CreateWebhookEndpoint } from "./components/create-webhook-endpoint";
import { WebhookEndpointsTable } from "./components/webhook-endpoints-table";
import { WebhookErrorText } from "./components/webhook-error-text";
import { WebhookEventsTable } from "./components/webhook-events-table";
import { webhookEndpointsQueryOptions } from "./webhooks.queries";
import { errorKey } from "./webhooks.utils";

export function WebhooksPage() {
	const t = useTranslations("webhooks");
	const locale = useLocale();
	const { data: endpoints, isPending, error } = useQuery(webhookEndpointsQueryOptions);
	const { data: billing } = useQuery(billingQueryOptions());
	// Where webhooks are off the server says so on every route, so the page offers nothing to add.
	const unavailable = errorKey(error) === "unavailable";

	return (
		<section className="mx-auto mt-2 flex w-full max-w-4xl flex-col gap-6">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<h1 className="text-2xl font-semibold">{t("title")}</h1>
				{billing && billing.billingStatus !== "active" && (
					<Notification variant="destructive">
						<CreditCard />
						<span>{t("billingInactive")}</span>
					</Notification>
				)}
				{!unavailable && <CreateWebhookEndpoint />}
			</div>

			<p className="text-muted-foreground text-sm">
				{t("intro")}{" "}
				<ExternalLink href={docsLink("webhooks", locale)}>{t("docsLink")}</ExternalLink>
			</p>

			{isPending ? (
				<Skeleton className="h-24 w-full" />
			) : unavailable ? (
				<WebhookErrorText error={error} />
			) : error ? (
				<p role="alert" className="text-destructive text-sm">
					{t("error")}
				</p>
			) : (
				<>
					<WebhookEndpointsTable endpoints={endpoints} />
					{endpoints.length > 0 && <WebhookEventsTable endpoints={endpoints} />}
				</>
			)}
		</section>
	);
}
